import * as z from 'zod';

import { Address, PaymentAddress } from '../Address';
import { CompanyId } from '../BrandedIds';
import { GeneralLedgerCodesReport } from '../companyReports/GeneralLedgerCodesReport';
import { EmailGroupKey } from '../emails/constants/emailGroups';
import { EmailSettings } from '../EmailSettings';
import { CompanyLogos } from '../Logos';
import { PortalSettings } from '../PortalSettings';
import { SupportHours } from '../SupportHours';
import { CompanyBillingSettings, DEFAULT_BILLING_SETTINGS } from './BillingSettings';
import { CompanyReportSettings } from './CompanyReportSettings';
import { CompanyThirdPartyIds } from './CompanyThirdPartyIds';
import { DocumentSettings } from './DocumentSettings';
import { CompanyFeeSettings } from './FeeSettings';
import { CompanyLoanControlSettings } from './LoanControlSettings';
import { CompanyNonPerformanceAutomationSettings, CompanyNonPerformanceSettings } from './NonPerformanceSettings';
import { CompanyTaskSettings } from './TaskSettings';

export interface Company {
  id: CompanyId;
  name: string;
  modalDisplayName: string;
  supportEmail: string;
  address?: Address;
  paymentAddress?: PaymentAddress;
  supportPhone: string;
  supportHours?: SupportHours;
  website?: string;
  emailSettings: EmailSettings;
  paymentsPaused: boolean;
  adminSettings: CompanyAdminSettings;
  logos: CompanyLogos;
  portalSettings: PortalSettings;
  documentSettings: DocumentSettings;
  nonPerformanceSettings: CompanyNonPerformanceSettings;
  nonPerformanceAutomationSettings: CompanyNonPerformanceAutomationSettings;
  taskSettings: CompanyTaskSettings;
  chatSettings?: CompanyChatSettings;
  feeSettings: CompanyFeeSettings;
  reportSettings?: CompanyReportSettings;
  generalLedgerCodesSettings?: GeneralLedgerCodesReport.Settings;
  ein?: string;
  loanControlSettings: CompanyLoanControlSettings;
  thirdPartyIds: CompanyThirdPartyIds;
  emailDomain?: string;
  emailFrom?: string;
  disabledEmailGroupKeys: EmailGroupKey[];
  ssoDomains: string[];
  authZeroConnectionNames: string[];
  portalDomain?: string;
}

export interface DwollaCompany {
  id: string;
  companyId: CompanyId;
  customerUrl: string;
  primaryBankAccount?: string;
  escrowBankAccount?: string;
  suspenseBankAccount?: string;
  sweepEnabled: boolean;
  plaidToken?: string;
  plaidAccountId?: string;
  plaidMicrodepositPostedEmailSent: boolean;
}

// There are demo features that have not been flushed out enough to move to WIP
const CompanyAdminSettingsForDemos = z.object({
  whedaEnabled: z.boolean().optional(),
});

const CompanyAdminSettingsForDocuments = z.object({
  loanExtensionAgreementDocumentEnabled: z.boolean().optional(), // elite only
  loanExtensionFeesDocumentEnabled: z.boolean().optional(), // elite only
});

export const CompanyAdminSettings = CompanyAdminSettingsForDemos.extend(CompanyAdminSettingsForDocuments.shape).extend({
  sendTransferEmailImmediately: z.boolean(),
  autopayEnabled: z.boolean(),
  mortgageeLettersEnabled: z.boolean(),
  investorRemittanceEnabled: z.boolean(),
  disableBorrowerNotifications: z.boolean().optional(), // FOLLOWUP: rename disableAllLoanNotifications
  portalBalanceForward: z.boolean().optional(),
  multiplePropertiesEnabled: z.boolean().optional(),
  generate1098Enabled: z.boolean().optional(),
  generate1098InterestThreshold: z.number().optional(),
  customerServiceBotEnabled: z.boolean().optional(),
  automateNonPerformingLoans: z.boolean(),
  enableUsioPayments: z.boolean().optional(),
  enableSameDayACH: z.boolean().optional(),
  enableEncryptedAccountDetailsStorage: z.boolean().optional(),
  enableBorrowerInsuranceExpirationEmailReminders: z.boolean().optional(),
  insuranceTaxTrackingEnabled: z.boolean(),
  newInsuranceFieldsEnabled: z.boolean().optional(),
  skipEscrowTrackingWithin60Days: z.boolean(),
  applicationsEnabled: z.boolean().optional(),
  applicationsEnabledSettings: z
    .object({
      ladderLife: z.boolean(),
    })
    .optional(),
  econsentEnabled: z.boolean().optional(),
  printAndMailEnabled: z.boolean().optional(),
  loanWorkflowsEnabled: z.boolean().optional(),
  chapter13WorkflowEnabled: z.boolean().optional(),
  billingSettings: CompanyBillingSettings,
  investorView: z.boolean().optional(),
  mfaAppUsersEnabled: z.boolean().optional(),
  creditPendingPaymentsOnPayoff: z.boolean().optional(),
  exactPaymentsOnly: z.boolean().optional(),

  forbearanceEnabled: z.boolean().optional(),
  noteflowEnabled: z.boolean().optional(),
  wipInvestorReporting: z.boolean().optional(),
  repayEnabled: z.boolean().optional(),
  glCodesReportEnabled: z.boolean().optional(),
  workflowsV2Enabled: z.boolean().optional(),
  applyPartialPayments: z.boolean().optional(),
  allowlistEmailDomains: z.array(z.string()).optional(),
  teamsEnabled: z.boolean().optional(),
  manualStatementGeneration: z.boolean().optional(),
  poolManagement: z.boolean().optional(),
  investorDocumentsEnabled: z.boolean().optional(),
  includePayoffFundingDateEnabled: z.boolean().optional(),
  eliteFees: z.boolean().optional(),
  wipCreditReporting: z.boolean().optional(),
  aiLoanNotesSummaryEnabled: z.boolean().optional(),
  mortgageInsuranceCancellationEligibilityReportEnabled: z.boolean().optional(),
  aiEmailsEnabled: z.boolean().optional(),
});

export type CompanyAdminSettings = z.infer<typeof CompanyAdminSettings>;
export type CompanyAdminSetting = keyof NonNullable<CompanyAdminSettings>;

export const DEFAULT_COMPANY_ADMIN_SETTINGS: Required<CompanyAdminSettings> = {
  // demo only
  whedaEnabled: false,

  // documents
  loanExtensionAgreementDocumentEnabled: false,
  loanExtensionFeesDocumentEnabled: false,

  // features
  sendTransferEmailImmediately: false,
  autopayEnabled: true,
  mortgageeLettersEnabled: false,
  investorRemittanceEnabled: false,
  disableBorrowerNotifications: false,
  portalBalanceForward: false,
  multiplePropertiesEnabled: false,
  generate1098Enabled: true,
  generate1098InterestThreshold: 600,
  customerServiceBotEnabled: false,
  automateNonPerformingLoans: false,
  enableUsioPayments: false,
  enableSameDayACH: false,
  enableEncryptedAccountDetailsStorage: true,
  enableBorrowerInsuranceExpirationEmailReminders: false,
  insuranceTaxTrackingEnabled: false,
  newInsuranceFieldsEnabled: false,
  skipEscrowTrackingWithin60Days: true,
  applicationsEnabled: false,
  applicationsEnabledSettings: {
    ladderLife: false,
  },
  econsentEnabled: false,
  printAndMailEnabled: false,
  loanWorkflowsEnabled: false,
  chapter13WorkflowEnabled: false,
  billingSettings: DEFAULT_BILLING_SETTINGS,
  investorView: false,
  mfaAppUsersEnabled: false,
  creditPendingPaymentsOnPayoff: false,
  exactPaymentsOnly: false,
  forbearanceEnabled: false,
  noteflowEnabled: false,
  wipInvestorReporting: false,
  repayEnabled: false,
  glCodesReportEnabled: false,
  workflowsV2Enabled: false,
  applyPartialPayments: false,
  allowlistEmailDomains: [],
  teamsEnabled: false,
  manualStatementGeneration: false,
  poolManagement: false,
  investorDocumentsEnabled: false,
  includePayoffFundingDateEnabled: false,
  eliteFees: false,
  wipCreditReporting: false,
  aiLoanNotesSummaryEnabled: false,
  mortgageInsuranceCancellationEligibilityReportEnabled: false,
  aiEmailsEnabled: false,
};

export const DEV_DEFAULT_COMPANY_ADMIN_SETTINGS: CompanyAdminSettings = {
  // demo only
  whedaEnabled: false,

  // features
  sendTransferEmailImmediately: false,
  autopayEnabled: true,
  mortgageeLettersEnabled: true,
  investorRemittanceEnabled: true,
  disableBorrowerNotifications: false,
  portalBalanceForward: true,
  multiplePropertiesEnabled: true,
  generate1098Enabled: true,
  generate1098InterestThreshold: 600,
  customerServiceBotEnabled: true,
  automateNonPerformingLoans: false,
  enableUsioPayments: true,
  enableSameDayACH: true,
  enableEncryptedAccountDetailsStorage: false,
  enableBorrowerInsuranceExpirationEmailReminders: true,
  insuranceTaxTrackingEnabled: true,
  newInsuranceFieldsEnabled: true,
  skipEscrowTrackingWithin60Days: false,
  applicationsEnabled: true,
  applicationsEnabledSettings: {
    ladderLife: true,
  },
  econsentEnabled: true,
  printAndMailEnabled: true,
  loanWorkflowsEnabled: false,
  chapter13WorkflowEnabled: true,
  billingSettings: DEFAULT_BILLING_SETTINGS,
  investorView: false,
  mfaAppUsersEnabled: false,
  creditPendingPaymentsOnPayoff: true,
  exactPaymentsOnly: false,
  forbearanceEnabled: true,
  noteflowEnabled: true,
  wipInvestorReporting: true,
  repayEnabled: false,
  glCodesReportEnabled: true,
  workflowsV2Enabled: true,
  teamsEnabled: true,
  manualStatementGeneration: true,
  poolManagement: true,

  // documents
  loanExtensionAgreementDocumentEnabled: false,
  loanExtensionFeesDocumentEnabled: false,

  investorDocumentsEnabled: true,
  includePayoffFundingDateEnabled: true,
  eliteFees: false,

  wipCreditReporting: false,
  aiLoanNotesSummaryEnabled: true,
  mortgageInsuranceCancellationEligibilityReportEnabled: true,
  aiEmailsEnabled: true,
};

export const DEFAULT_COMPANY_LOGOS: CompanyLogos = {
  portalLogo: false,
  emailLogo: false,
};

export const CompanyChatSettings = z.object({
  chatGptContext: z.string().optional(),
});
export type CompanyChatSettings = z.infer<typeof CompanyChatSettings>;
